import { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, ApiError, withSessionRenewal } from "../api/client.js";
import { useSession } from "../auth/session.js";
import { Breadcrumb } from "../shell/Shell.js";
import { ConfidencePill, EmptyState } from "../components.js";
import { Button } from "../components/controls.js";
import type { Role } from "../roles.js";

type ReviewQueueKind = "identity_resolution" | "evidence_link";
type ReviewQueueAction = "confirm" | "reject";

interface ReviewQueueBase {
  readonly kind: ReviewQueueKind;
  readonly factId: number;
  readonly status: "flagged";
  readonly method: string;
  readonly confidence: number;
  readonly sourceEventIds: readonly number[];
  readonly note: string | null;
  readonly effectiveAt: string;
}

interface IdentityQueueItem extends ReviewQueueBase {
  readonly kind: "identity_resolution";
  readonly identityId: string;
  readonly proposedPersonId: string;
}

interface EvidenceQueueItem extends ReviewQueueBase {
  readonly kind: "evidence_link";
  readonly fromKind: string;
  readonly fromId: string;
  readonly toKind: string;
  readonly toId: string;
  readonly key: string;
}

type ReviewQueueItem = IdentityQueueItem | EvidenceQueueItem;

const REVIEWER_ROLES: readonly Role[] = ["admin", "delivery-lead"];

function canReview(role: Role): boolean {
  return REVIEWER_ROLES.includes(role);
}

function labelFor(item: ReviewQueueItem): string {
  if (item.kind === "identity_resolution") {
    return `identity ${item.identityId} → proposed person ${item.proposedPersonId}`;
  }
  return `${item.fromKind} ${item.fromId} ↔ ${item.toKind} ${item.toId} (${item.key})`;
}

export function ReviewQueue(): React.ReactNode {
  const { session, onUnauthenticated } = useSession();
  const token = session?.token ?? null;
  const [items, setItems] = useState<ReviewQueueItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const forbidden = session ? !canReview(session.role) : false;

  useEffect(() => {
    if (!token || forbidden) return;
    const controller = new AbortController();
    setItems(null);
    setError(null);
    apiGet<ReviewQueueItem[]>("/review-queue", { token, signal: controller.signal })
      .then((envelope) => setItems(envelope.data))
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        const apiError =
          caught instanceof ApiError ? caught : new ApiError(0, "unknown", "unexpected error");
        if (apiError.isUnauthenticated) {
          void onUnauthenticated(token);
          return;
        }
        setError(apiError.message);
      });
    return () => controller.abort();
  }, [forbidden, token, onUnauthenticated]);

  async function act(item: ReviewQueueItem, action: ReviewQueueAction): Promise<void> {
    if (!token) return;
    const key = `${item.kind}:${item.factId}:${action}`;
    setPending(key);
    setError(null);
    try {
      // NX-3433: a 401 renews the session and retries this same decision once.
      await withSessionRenewal(token, onUnauthenticated, (fresh) =>
        apiPost(`/review-queue/${item.kind}/${item.factId}/${action}`, { token: fresh }),
      );
      setItems((current) =>
        (current ?? []).filter(
          (candidate) => !(candidate.kind === item.kind && candidate.factId === item.factId),
        ),
      );
    } catch (caught: unknown) {
      const apiError =
        caught instanceof ApiError ? caught : new ApiError(0, "unknown", "unexpected error");
      if (apiError.isUnauthenticated) return; // renewal failed: the session has ended
      setError(apiError.message);
    } finally {
      setPending(null);
    }
  }

  const rows = useMemo(() => items ?? [], [items]);

  return (
    <>
      <Breadcrumb items={[{ label: "Portfolio", to: "/portfolio" }, { label: "Review queue" }]} />
      <div className="head">
        <h1>Review queue</h1>
      </div>
      <div className="meta">
        When SeneSight links identities (two author handles that look like one person) or evidence
        (a commit ↔ work item ↔ deployment chain) at LOW confidence, it flags the link here for a
        person to confirm or reject instead of silently trusting it. Confirmed links strengthen the
        figures; rejected ones are reversed with full audit lineage.
      </div>
      {forbidden && (
        <EmptyState message="You are signed in, but your role is not authorized to review flagged facts." />
      )}
      {!forbidden && items === null && !error && (
        <div className="meta">Loading flagged identity/evidence facts…</div>
      )}
      {!forbidden && error && <EmptyState message={`Couldn't load the review queue: ${error}`} />}
      {!forbidden && items !== null && !error && rows.length === 0 && (
        <EmptyState message="Nothing is flagged — every identity and evidence link so far cleared the confidence threshold on its own. An empty queue is the healthy state." />
      )}
      {!forbidden && rows.length > 0 && (
        <div className="panel pad">
          {rows.map((item) => {
            const confirmKey = `${item.kind}:${item.factId}:confirm`;
            const rejectKey = `${item.kind}:${item.factId}:reject`;
            return (
              <div
                key={`${item.kind}:${item.factId}`}
                className="childrow"
                style={{ display: "grid", gap: 10, marginBottom: 10 }}
              >
                <div>
                  <div className="cn">
                    {item.kind === "identity_resolution"
                      ? "Identity proposal"
                      : "Evidence proposal"}{" "}
                    #{item.factId}
                  </div>
                  <div className="ck">{labelFor(item)}</div>
                  <div className="ck">Method: {item.method}</div>
                  <div className="ck">
                    <ConfidencePill value={item.confidence} /> · Source events:{" "}
                    {item.sourceEventIds.length}
                  </div>
                  {item.note && <div className="ck">Note: {item.note}</div>}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    size="sm"
                    onClick={() => void act(item, "confirm")}
                    disabled={pending === confirmKey || pending === rejectKey}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => void act(item, "reject")}
                    disabled={pending === confirmKey || pending === rejectKey}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
